import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import {
    FileTransfersService,
    IOcbEnvironmentAccess,
    IOcbFileTransfer,
    IOcbLocalAgentCommand,
    LocalAgentsService,
    OcbLocalAgentCommandStatus,
    UserService
} from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin } from 'rxjs';
import { LocalAgentCommandComponent } from '../local-agent-command-popup/local-agent-command-popup.component';

@Component({
    selector: 'app-file-transfer-execution',
    templateUrl: './file-transfer-execution-popup.component.html',
    styleUrls: ['./file-transfer-execution-popup.component.scss'],
})
export class FileTransferExecutionComponent implements AfterViewInit, OnDestroy {
    @Input()
    public fileTransferId?: string;
    @Input()
    public visible: boolean = false;

    @ViewChild('agentCommandPopup')
    public agentCommandPopup: LocalAgentCommandComponent;
    @ViewChild('ftSelectBox')
    public ftSelectBox: DxSelectBoxComponent;

    public currentEnvironment?: IOcbEnvironmentAccess;
    public fileTransfers: IOcbFileTransfer[] = [];
    public isTransferInProgress: boolean = false;
    public selectedFileTransferId?: string;
    public transferProgression: string = '';
    public transferStep: number = 0;

    private commandCheckTimer;
    private hideTransferProgressionTimer;
    private statusLabels: any;

    private checkTransfer(command: IOcbLocalAgentCommand) {
        this.transferProgression = this.statusLabels[OcbLocalAgentCommandStatus[command.status]];

        if (command.status == OcbLocalAgentCommandStatus.Running) {
            this.transferStep = 2;
            this.commandCheckTimer = setTimeout(() => this.getAndCheckCommand(command.localAgent.id, command.id), 3000);
        }
        else if (command.status == OcbLocalAgentCommandStatus.Done) {
            this.transferStep = 3;
            forkJoin([
                this.translateService.get('fileTransfers.msgTransferCompleted'),
                this.translateService.get('fileTransfers.msgTransferCompletedNoFileTransferred'),
                this.translateService.get('fileTransfers.msgTransferCompletedFileTransferred'),
                this.translateService.get('fileTransfers.msgTransferCompletedFilesTransferred')
            ])
                .subscribe({
                    next: (messages) => {
                        const filesCount = command.fileTransferLogs.length;
                        let message = '';
                        if (filesCount == 0) message = `${messages[0]}, ${messages[1]}`;
                        else if (filesCount == 1) message = `${messages[0]}, ${filesCount} ${messages[2]}`;
                        else message = `${messages[0]}, ${filesCount} ${messages[3]}`;

                        notify(message, 'success', 8000);
                        this.hideTransferProgressionTimer = setTimeout(() => this.hideTransferProgression(), 2000);
                        this.showCommandPopup(command);
                    },
                    error: (error) => this.handleError(error)
                });
        }
        else if (command.status == OcbLocalAgentCommandStatus.Error) {
            this.transferStep = 3;
            this.translateService.get('fileTransfers.msgTransferCompletedWithError').subscribe({
                next: (message) => {
                    notify(message, 'error', 8000);
                    this.hideTransferProgressionTimer = setTimeout(() => this.hideTransferProgression(), 2000);
                    this.showCommandPopup(command);
                },
                error: (error) => this.handleError(error)
            });
        }
        else if (command.status == OcbLocalAgentCommandStatus.Expired) {
            this.transferStep = 3;
            this.translateService.get('fileTransfers.msgTransferExpired').subscribe({
                next: (message) => {
                    notify(message, 'error', 8000);
                    this.hideTransferProgressionTimer = setTimeout(() => this.hideTransferProgression(), 2000);
                    this.showCommandPopup(command);
                },
                error: (error) => this.handleError(error)
            });
        }
        else {
            //status: Waiting
            this.transferStep = 1;
            this.commandCheckTimer = setTimeout(() => this.getAndCheckCommand(command.localAgent.id, command.id), 3000);
        }
    }

    private getAndCheckCommand(agentId, commandId) {
        this.localAgentsService.getCommand(agentId, commandId).subscribe({
            next: (command) => this.checkTransfer(command),
            error: (error) => {
                this.handleError(error);
                this.isTransferInProgress = false;
                this.transferProgression = '';
            }
        });
    }

    private hideTransferProgression() {
        this.isTransferInProgress = false;
        this.transferProgression = '';
    }

    private showCommandPopup(command: IOcbLocalAgentCommand) {
        this.agentCommandPopup.agentCommand = command;
        this.agentCommandPopup.visible = true;
    }

    constructor(private userService: UserService,
        private translateService: TranslateService,
        private fileTransfersService: FileTransfersService,
        private localAgentsService: LocalAgentsService) { }

    public ngAfterViewInit(): void { this.translateService.get('localAgentsCommandsStatus').subscribe(labels => this.statusLabels = labels); }

    public ngOnDestroy() {
        if (this.commandCheckTimer) clearTimeout(this.commandCheckTimer);
        if (this.hideTransferProgressionTimer) clearTimeout(this.hideTransferProgressionTimer);
    }

    public onHidden() {
        this.currentEnvironment = null;
        this.fileTransfers = null;
        this.isTransferInProgress = false;
        this.selectedFileTransferId = null;
        this.transferProgression = '';
        this.transferStep = 0;
        if (this.commandCheckTimer) clearTimeout(this.commandCheckTimer);
        if (this.hideTransferProgressionTimer) clearTimeout(this.hideTransferProgressionTimer);
    }

    public onShown() {
        this.userService.user.subscribe(admin => {
            this.currentEnvironment = admin.environments.find(env => env.environmentId === this.userService.currentEnvironment);
            this.loadFileTransfers();
        });
    }

    public onTransferChanged(e) {
        this.selectedFileTransferId = e.selectedItem?.id;
    }

    public loadFileTransfers(): void {
        if (!this.currentEnvironment) return;
        this.fileTransfersService.getFileTransfers(this.currentEnvironment.environmentId)
            .subscribe(fileTransfers => {
                this.fileTransfers = fileTransfers;
                if (this.fileTransferId) this.ftSelectBox.value = this.fileTransfers.find(ft => ft.id === this.fileTransferId)?.id;
            });
    }

    public executeFileTransfer(): void {
        if (!this.selectedFileTransferId) return;
        forkJoin([
            this.translateService.get('fileTransfers.msgExecuteConfirm'),
            this.translateService.get('fileTransfers.msgExecuteConfirmTitle')
        ]).subscribe({
            next: (messages) => {
                confirm(messages[0], messages[1]).then((confirmed) => {
                    if (!confirmed) return;
                    this.transferStep = 0;
                    this.fileTransfersService.executeFileTransfer(this.selectedFileTransferId, false).subscribe({
                        next: (command) => {
                            this.isTransferInProgress = true;
                            this.checkTransfer(command);
                        },
                        error: (error) => this.handleError(error)
                    });
                });
            },
            error: (err: any) => this.handleError(err)
        });
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}